'use client';

import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

export type ClientNames =
  /** next.frame.io */
  | 'web'
  /** Adobe UXP host product ids (integrations.frame.io) */
  | 'premiere'
  | 'aftereffects'
  /** Via send to Frame web embed (/export-picker iframe) */
  | 'firefly_boards'
  /** Host not yet resolved (e.g. browser dev before UXP host info loads) */
  | 'unknown';

export type IntegrationHostName = Exclude<
  ClientNames,
  'web' | 'unknown' | 'firefly_boards'
>;

/**
 * Client name controller context. Used to determine which client is currently
 * being used.
 */
const ClientNameContext = createContext({ name: null as ClientNames | null });

/**
 * Use this Context Provider at the top level of an app. Contexts in other
 * packages can consume this Context to override the values they provide. Avoid
 * this context when it's possible to either hoist participating contexts to
 * higher levels, or use prop drilling.
 */
export function ClientNameContextProvider(
  props: PropsWithChildren<{ name: ClientNames }>
) {
  const [name, setName] = useState<ClientNames>(props.name);
  const context = useMemo(() => ({ name, setName }), [name]);

  useEffect(() => {
    if (name !== props.name) {
      setName(props.name);
    }
  }, [name, props.name]);

  return (
    <ClientNameContext.Provider value={context}>
      {props.children}
    </ClientNameContext.Provider>
  );
}

/**
 * Context for client name state. Used to determine which client is currently
 * being used.
 *
 * @see ClientNameContextProvider
 */
export const useClientNameContext = () =>
  useContextSelector(ClientNameContext, (context) => context.name);

/**
 * Checks if the current client name matches the given name
 */
export const useIsMatchingClientName = (name: ClientNames) =>
  useContextSelector(ClientNameContext, (context) => context.name === name);

/**
 * True when running inside a UXP integration host (Premiere Pro or After Effects).
 */
export const useIsIntegrationClient = () => {
  const name = useClientNameContext();
  return name === 'premiere' || name === 'aftereffects';
};
